import type { ComponentType, SVGProps } from 'react';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export type PillTint = 'gray' | 'blue' | 'green' | 'amber' | 'red' | 'neutral';

export interface SegmentPill {
  key: string;
  label: string;
  /** Baseline count shown before segment-counts override loads. Use null to render '—' until override arrives. */
  count: number | null;
  /** Per-pill colour tint matching the corresponding card badge. Defaults to 'neutral'. */
  tint?: PillTint;
  /** Lucide icon component (preferred — modern, consistent with the rest of the app). */
  Icon?: IconComponent;
  /** Tailwind text-color class applied to the icon (e.g. "text-red-500"). */
  iconClass?: string;
}

interface SegmentPillsBase {
  pills: SegmentPill[];
  /** Optional override counts keyed by pill.key (from /api/people/segment-counts or /api/organizations/segment-counts). */
  counts?: Record<string, number>;
  /** When true, renders '—' in place of count numbers. */
  isLoadingCounts?: boolean;
  /** Associates the group with an external label element via aria-labelledby. */
  'aria-labelledby'?: string;
}
interface SingleSelectProps extends SegmentPillsBase {
  multiSelect?: false;
  activeKey: string;
  onSelect: (key: string) => void;
}
interface MultiSelectProps extends SegmentPillsBase {
  multiSelect: true;
  activeKeys: string[];
  onToggle: (key: string) => void;
}
type Props = SingleSelectProps | MultiSelectProps;

const TINT_CLASSES: Record<PillTint, string> = {
  gray:    'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
  blue:    'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
  green:   'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
  amber:   'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  red:     'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
  neutral: 'bg-white text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600',
};

const SELECTED_RING = 'shadow-[inset_0_0_0_2px_currentColor] font-bold';

export const SegmentPills = (props: Props) => {
  const isActive = (key: string) =>
    props.multiSelect
      ? props.activeKeys.includes(key)
      : props.activeKey === key;

  const handle = (key: string) =>
    props.multiSelect ? props.onToggle(key) : props.onSelect(key);

  const { counts, isLoadingCounts } = props;
  const ariaLabelledby = props['aria-labelledby'];

  return (
    <div
      role={props.multiSelect ? 'group' : 'radiogroup'}
      aria-labelledby={ariaLabelledby}
      className="flex flex-wrap gap-2"
    >
      {props.pills.map((pill) => {
        const active = isActive(pill.key);
        const tint = pill.tint ?? 'neutral';
        const Icon = pill.Icon;
        // Use override count from segment-counts endpoint when provided, else fall back to pill.count
        const displayCount = counts !== undefined
          ? (isLoadingCounts ? null : (counts[pill.key] ?? pill.count))
          : pill.count;
        return (
          <button
            key={pill.key}
            type="button"
            role={props.multiSelect ? 'checkbox' : 'radio'}
            aria-checked={active}
            onClick={() => handle(pill.key)}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1 rounded-full text-sm cursor-pointer border transition-colors ${TINT_CLASSES[tint]} ${active ? SELECTED_RING : ''}`}
          >
            {active && <span aria-hidden>✓</span>}
            {Icon && <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${pill.iconClass ?? ''}`} />}
            <span>{pill.label}</span>
            <span className={`ml-0.5 tabular-nums text-xs ${active ? 'opacity-70' : 'text-gray-400 dark:text-gray-500'}`}>
              {displayCount === null ? '—' : displayCount.toLocaleString()}
            </span>
          </button>
        );
      })}
    </div>
  );
};
